<template>
  <div   
    :class="[
      player.active ? 'bg-red-800 text-white' : 'bg-gray-200 text-gray-400',
      gameStore.highlightedPlayerId === player.id ? 'animate-foul-alert ring-4 ring-red-500 z-20' : ''
    ]"
    class="p-2 w-1/5 transition-colors duration-300 flex justify-start items-center border border-gray-300 rounded-lg select-none"
    @click.stop="handleClick(team.name == gameStore.match?.homeTeam.name ? 0 : 1)" 
  >
    <span class="truncate flex-1 min-w-0">
      {{ player.number }}. {{ player.name }}
    </span>

    <div class="flex items-center gap-1.5 ml-auto shrink-0">
      <div 
        v-if="player.isGK" 
        class="px-1.5 py-0.5 border rounded-2xl text-xs  transition-all duration-300"
        :class="[player.active ? 'bg-white border-white text-red-700' : '']"
      >
        GK
      </div>

      <ExclamationTriangleIcon 
        v-if="gameStore.isOut(player)"
        class="size-4 text-red-500 animate-pulse" 
      />
    </div>
  </div>

  <div v-if="team.activatedTimer && canEditMatch(userRole)" class=" ms-1 flex flex-col gap-1">
    <div class="flex items-center gap-1 text-blue-950">
      
      <BoltIcon v-if="player.active" class="w-5 h-5 text-red-800" />
      <EllipsisHorizontalCircleIcon v-else class="w-5 h-5 text-gray-400" />
      
      <span class="tabular-nums">{{ useTimeFormat().formatMsToTimer(player.actualTime) }}</span>
    </div>
  </div>

  <div v-if="settings.enableFouls" class="inline-flex items-start ml-2 gap-1 " role="group">
    <ExclutionButton 
      :disabled="!canEditMatch(userRole)"
      :team="team.name == gameStore.match?.homeTeam.name ? 0 : 1"
      :exclution-state="getExclutionState(0)"
      @handleFoul="addFoul($event, 0)"
      @handleEDCS="addEDCS($event, 0)" 
      @remove="removeExclution(0)" />
    <ExclutionButton 
      :disabled="!canEditMatch(userRole)"
      :team="team.name == gameStore.match?.homeTeam.name ? 0 : 1"
      :exclution-state="getExclutionState(1)"
      @handleFoul="addFoul($event, 1)"
      @handleEDCS="addEDCS($event, 1)" 
      @remove="removeExclution(1)" />
    <ExclutionButton 
      :disabled="!canEditMatch(userRole)"
      :team="team.name == gameStore.match?.homeTeam.name ? 0 : 1"
      :exclution-state="getExclutionState(2)"
      @handleFoul="addFoul($event, 2)" 
      @handleEDCS="addEDCS($event, 2)" 
      @remove="removeExclution(2)" />
  </div>

  <template v-if="settings.enableShots">
    <div class="inline-flex items-center ml-2 gap-1 text-blue-950" role="group">
      <div class="h-6 w-8 flex items-center justify-end">PARI</div>
      <ShotButton v-if="canEditMatch(userRole)"
        :disabled="!player.active" 
        :type="ShotCategory.EVEN" 
        @handleShot="addShot"
        @remove-shot="removeShot"/>
      <div class="h-6 w-8 flex items-center"> {{ playerShots?.even.filter(shot => shot.shotOutcome === ShotOutcome.GOAL ).length + '/' + playerShots?.even.length }}</div>
    </div>
    <div class="inline-flex items-center justify-center ml-2 gap-1 text-blue-950" role="group">
      <div class="h-6 w-8 inline-flex items-center justify-end" >SUP</div>
      <ShotButton v-if="canEditMatch(userRole)"
        :disabled="!player.active" 
        :type="ShotCategory.SUP" 
        :is-goal="false" 
        @handleShot="addShot"
        @remove-shot="removeShot"/>
      <div class="h-6 w-8 inline-flex items-center tabular-nums"> {{ playerShots?.sup.filter(shot => shot.shotOutcome === ShotOutcome.GOAL ).length + '/' + playerShots?.sup.length }}</div>
    </div>
    <div class="inline-flex items-center ml-2 gap-1 text-blue-950" role="group">
      <div class="h-6 w-8 flex items-center justify-end">RIG</div>
      <ShotButton v-if="canEditMatch(userRole)"
        :disabled="!player.active" 
        :type="ShotCategory.PENALTY" 
        :is-goal="false" 
        @handleShot="addShot"
        @remove-shot="removeShot"/>
      <div class="h-6 w-8 flex items-center"> {{ playerShots?.penalty.filter(shot => shot.shotOutcome === ShotOutcome.GOAL ).length + '/' + playerShots?.penalty.length }}</div>
    </div>
    <div class="inline-flex items-center ml-2 text-blue-950" role="group">
      <div class="h-6 w-8 flex items-center font-bold text-base"> 
        <span v-if="player.isGK">
          {{ playerShotsFaced?.saves.length + '/' + playerShotsFaced?.shots.length }}
        </span>
        <span v-else>
          {{ gameStore.getAllPlayerShots(player, null).goals + '/' + gameStore.getAllPlayerShots(player, null).shots }}
        </span>
      </div>
    </div>

    <div class="h-6 w-6 flex justify-center items-center mr-2">
      <InformationCircleIcon class="size-6 text-blue-950" @click="openPlayerDetailModal"/>
    </div>

  </template>

  <PlayerDetailModal :isOpen="isPlayerDetailOpen" :player="player" :team="team"
          @close="isPlayerDetailOpen = false" />

</template>

<script setup lang="ts">
import { useGameStore } from "@/stores/gameStore";
import { ref, type PropType, computed } from "vue";
import type { Player } from "@/interfaces/Player";
import type { Team } from "../interfaces/Team";
import ExclutionButton from "./buttons/ExclutionButton.vue";
import { BoltIcon, EllipsisHorizontalCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/vue/24/outline";
import ShotButton from "./buttons/ShotButton.vue";
import { ShotCategory, ShotOutcome } from '@/enum/ShotDescription';
import { useSettingsStore } from "@/stores/settingsStore";
import PlayerDetailModal from "./modals/PlayerDetailModal.vue";
import { foulCategoryLabels, foulPositionLabels } from "@/const/consts";
import { formatTime, getLabel } from "@/utils/utils";
import { FoulType, type EDCSType, type FoulPosition } from "@/enum/ExclutionDescription";
import { useTimeFormat } from "@/composables/useTimeFormat";
import { useMatchStateStore } from "@/stores/matchStateStore";
import { usePermissions } from "@/composables/usePermissions";

const props = defineProps({
  player: {
    type: Object as PropType<Player>,
    required: true,
  },
  team: {
    type: Object as PropType<Team>,
    required: true,
  },
});

const gameStore = useGameStore();
const settings = useSettingsStore();
const isPlayerDetailOpen = ref<boolean>(false);
const matchStateStore = useMatchStateStore();
const { canEditMatch } = usePermissions();
const userRole = computed(() => matchStateStore.userRole);

const playerShots = computed(() => {
  if(props.player.id) 
    return gameStore.getPlayerShotsByCategory(props.player.id)
  }
);

const playerShotsFaced = computed(() => {
  if(props.player.id && props.player.isGK) 
    return gameStore.getGoalkeeperShotsFaced(props.player.id)
  }
);

const handleClick = (team: number) => {
  if (!canEditMatch(userRole.value)) return;
  gameStore.toggleElement(props.player.number, team);
};

const addFoul = (payload : { type: string, position: string, ball: boolean, earnedBy: number}, exclNumber: number) => {
  gameStore.processFoul({
    number: props.player.number, 
    team: (props.team.name === gameStore.match?.homeTeam.name ? 0 : 1), 
    type: payload.type as FoulType, 
    exclNumber: exclNumber, 
    position: payload.position as FoulPosition, 
    ball: payload.ball, 
    earnedBy: payload.earnedBy
  });
}
;
const addEDCS = (payload : { type: string, edcsType: string }, exclNumber: number) => {
  gameStore.processFoul({
    number: props.player.number, 
    team: (props.team.name === gameStore.match?.homeTeam.name ? 0 : 1), 
    type: payload.type as FoulType, 
    edcsType: payload.edcsType as EDCSType, 
    exclNumber:exclNumber
  });
};

const addShot = (payload : { type: ShotCategory, position: string, outcome: ShotOutcome }) => {
  gameStore.addShot(props.player.number, (props.team.name === gameStore.match?.homeTeam.name ? 0 : 1), payload.type, payload.position, payload.outcome)
};

const removeShot = (payload : { type: ShotCategory }) => {
  gameStore.removeShot(props.player.number, (props.team.name === gameStore.match?.homeTeam.name ? 0 : 1), payload.type)
};

const removeExclution = (exclNumber: number) => {
  gameStore.removeExclution(props.player.number, (props.team.name === gameStore.match?.homeTeam.name ? 0 : 1), exclNumber);
};

const getExclutionState = (index: number) => {
  if(props.player.id){

    const excl = gameStore.getPlayerFouls(props.player.id)[index];
    if(excl) {
      if(excl.foulType === FoulType.EDCS){
        return getLabel(excl.foulType, foulCategoryLabels).toUpperCase().substring(0,2);
      }
      else {
        return getLabel(excl.foulType, foulCategoryLabels).charAt(0) + '-' + getLabel(excl.foulPosition, foulPositionLabels).charAt(0)
      }
    }
  }
  return '';
}

const openPlayerDetailModal = () => {
  isPlayerDetailOpen.value = true;
}

</script>